import React, {useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Util} from 'yougile-util'
import {noPropagate, useGlobalKeyDown} from '@containers/hooks'
import {MoveToBody} from './move-to-body'

const TwMoveToBody = tw(MoveToBody)<{$bottomZero: boolean}>`
  ${p => (p.$bottomZero ? 'bottom-0' : '')}
`

export interface Props extends React.HTMLProps<HTMLDivElement> {
  boundRight?: number
  zIndex?: number
  keepPosition?: boolean
  storeMaxHeight?: (n: number) => void
  withPropagate?: boolean
  recalcWhenResize?: boolean
  bottomZero?: boolean
  onEscape: () => void
}

const calcPos = (el: HTMLElement): {x: number; y: number} => {
  const pos = {x: 0, y: 0}
  if (el) {
    const rect = el.getBoundingClientRect()
    pos.x = rect.left
    pos.y = rect.top
  }
  const maxY = window.innerHeight
  const maxX = window.innerWidth
  pos.x = Math.max(0, Math.min(maxX, pos.x))
  pos.y = Math.max(0, Math.min(maxY, pos.y))
  return pos
}

const useResizeIndex = (needResizeIndex: boolean) => {
  const [recalcIndex, setRecalcIndex] = useState(0)

  useEffect(() => {
    if (!needResizeIndex) {
      return undefined
    }

    const listener = () => {
      setRecalcIndex(Date.now())
    }

    window.addEventListener('resize', listener)

    return () => {
      window.removeEventListener('resize', listener)
    }
  }, [needResizeIndex, setRecalcIndex])

  return recalcIndex
}

const viewportPadding = 10
const minMenuHeight = 200

export const Menu = (props: Props): JSX.Element => {
  const {
    storeMaxHeight,
    boundRight = 0,
    zIndex,
    keepPosition = false,
    withPropagate,
    bottomZero = false,
    recalcWhenResize,
    onEscape,
    ...htmlProps
  } = props
  const [keepPos, setPos] = useState<{x?: number; y?: number}>({})
  const [x, setX] = useState(0)
  const [y, setY] = useState(0)
  const [maxHeight, setMaxHeight] = useState(0)
  const [className, setClassName] = useState(props.className ?? '')
  const ref = useRef(null)
  const timeoutHandler = useRef<TimeoutHandler>()
  const resizeIndex = useResizeIndex(!!recalcWhenResize)

  useEffect(() => {
    if (!ref.current) {
      return () => {
        clearTimeout(timeoutHandler.current)
      }
    }
    const pos = calcPos(ref.current)
    const maxH = window.innerHeight - pos.y - viewportPadding
    const maxW = window.innerWidth - pos.x

    const downDir = maxH >= minMenuHeight
    const dirY = downDir ? 'down' : 'up'
    const dirX = maxW > 300 || maxW > pos.x ? 'right' : 'left'
    const dirClassName = `direction-${dirX} direction-${dirY} direction-${dirY}-${dirX}`
    const boundClass = pos.x > boundRight ? 'menu--overflow-right' : ''

    setMaxHeight(downDir ? maxH : pos.y + viewportPadding)
    storeMaxHeight?.(downDir ? maxH : pos.y + viewportPadding)
    setClassName([props.className || '', dirClassName, boundClass].join(' '))
    setX((keepPosition ? keepPos.x : pos.x) ?? pos.x)
    setY((keepPosition ? keepPos.y : pos.y) ?? pos.y)

    if (keepPos.x === undefined) {
      timeoutHandler.current = setTimeout(() => {
        setPos({...pos})
      }, 50)
    }
    return () => {
      clearTimeout(timeoutHandler.current)
    }
  }, [ref.current, resizeIndex, keepPos, keepPosition, boundRight])

  const onMouseEvent = (e: React.MouseEvent) => {
    withPropagate ? Util.noop() : noPropagate(e)
  }

  useGlobalKeyDown({
    escape: () => {
      onEscape()
      return true
    }
  })

  return (
    <div ref={ref} onClick={onMouseEvent} onMouseDown={onMouseEvent}>
      {ref.current && (!!x || !!y) && (
        <TwMoveToBody left={x} top={y} zIndex={zIndex} $bottomZero={bottomZero}>
          <div style={{maxHeight}} {...htmlProps} className={className} />
        </TwMoveToBody>
      )}
    </div>
  )
}
